import { useApolloClient } from "@apollo/client";
import { useEffect } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { AGREEMENTS_RUN_PLANNED_SUBSCRIPTION, AGREEMENT_RUN_SUBSCRIPTION } from "../containers/agreements/agreement/queries";
import { OnAgreementRunSubscription, OnAgreementsRunPlannedSubscription } from "../containers/agreements/agreement/types/queries.generated";
import { AGREEMENTS_SUBSCRIPTION } from "../containers/agreements/queries";
import { OnAgreementCreatedSubscription } from "../containers/agreements/types/queries.generated";
import { CASES_SUBSCRIPTION } from "../containers/cases/queries";
import { OnCaseCreatedSubscription } from "../containers/cases/types/queries.generated";
import {
	FLEX_ENTITY_BULK_SUBSCRIPTION,
	FLEX_ENTITY_UPLOAD_SUBSCRIPTION,
	FORMS_SUBSCRIPTION,
	RECORDS_SUBSCRIPTION,
	SIMULATION_SUBSCRIPTION,
} from "../containers/data/queries";
import {
	OnFlexEntityBulkSubscription,
	OnFlexEntityUploadSubscription,
	OnFormCreatedSubscription,
	OnRecordUpdatedSubscription,
	OnSimulationUpdatedSubscription,
} from "../containers/data/types/queries.generated";
import { DOCUMENTS_SUBSCRIPTION } from "../containers/financials/documents/queries";
import { OnDocumentCreatedSubscription } from "../containers/financials/documents/types/queries.generated";
import { REFUNDS_SUBSCRIPTION } from "../containers/financials/refunds/queries";
import { OnRefundCreatedSubscription } from "../containers/financials/refunds/types/queries.generated";
import { OnTaskUpdatedSubscription } from "../containers/home/tasks/types/queries.generated";
import { QUESTS_SUBSCRIPTION } from "../containers/layout/inbox/quests/queries";
import { OnQuestUpdatedSubscription } from "../containers/layout/inbox/quests/types/queries.generated";
import { TASKS_SUBSCRIPTION } from "../containers/layout/inbox/tasks/queries";
import { useDataSubscription } from "../hooks/useDataModule";
import { AgreementRunStatus } from "../models/generated/graphql";
import { activeAgreementReport, agreementExecutionTotalCount, agreementsExecution } from "../store/agreements";
import { isDataCreationDisabled } from "../store/data";
import { bulk, upload } from "../store/upload";
import { useModelsSubscription } from "../hooks/useModelsModule";
import { THREAD_SUBSCRIPTION } from "../containers/cases/messages/queries";
import { OnMessageUpdatedSubscription } from "../containers/cases/messages/types/queries.generated";
import { DOCUMENT_THREAD_SUBSCRIPTION } from "../containers/financials/documents/messaging/queries";
import { OnDocumentThreadUpdatedSubscription } from "../containers/financials/documents/messaging/types/queries.generated";
import { incomingMessages } from "../store/documents";

interface RootSubscriptionsProps {
	tenantId: string;
	userId: string;
}

/**
 * @description Used for situations where a user is directed to a component and the subscription event
 * has occurred before the target component was mounted
 * @param param0 tenantId: the current tenant
 * @returns null and forces a state update to the target component by evicting the cache. This is more
 * approach has performance advantages to using Apollo's 'network-and-cache' fetch policy
 */

export function RootSubscriptions({ tenantId, userId }: RootSubscriptionsProps) {
	const client = useApolloClient();

	const { data: agreements } = useDataSubscription<OnAgreementCreatedSubscription>(AGREEMENTS_SUBSCRIPTION);
	const { data: cases } = useDataSubscription<OnCaseCreatedSubscription>(CASES_SUBSCRIPTION);
	const { data: forms } = useDataSubscription<OnFormCreatedSubscription>(FORMS_SUBSCRIPTION);
	const { data: records } = useDataSubscription<OnRecordUpdatedSubscription>(RECORDS_SUBSCRIPTION);
	const { data: refunds } = useDataSubscription<OnRefundCreatedSubscription>(REFUNDS_SUBSCRIPTION);
	const { data: documents } = useDataSubscription<OnDocumentCreatedSubscription>(DOCUMENTS_SUBSCRIPTION);
	const { data: tasks } = useDataSubscription<OnTaskUpdatedSubscription>(TASKS_SUBSCRIPTION);
	const { data: quests } = useDataSubscription<OnQuestUpdatedSubscription>(QUESTS_SUBSCRIPTION);
	const { data: simulations } = useModelsSubscription<OnSimulationUpdatedSubscription>(SIMULATION_SUBSCRIPTION);
	const { data: messages } = useDataSubscription<OnMessageUpdatedSubscription>(THREAD_SUBSCRIPTION);
	const { data: agreement } = useDataSubscription<OnAgreementRunSubscription>(AGREEMENT_RUN_SUBSCRIPTION);
	const { data: plannedAgreementsRun } = useDataSubscription<OnAgreementsRunPlannedSubscription>(AGREEMENTS_RUN_PLANNED_SUBSCRIPTION);
	const { data: flexEntityUpload } = useDataSubscription<OnFlexEntityUploadSubscription>(FLEX_ENTITY_UPLOAD_SUBSCRIPTION);
	const { data: flexEntityBulk } = useDataSubscription<OnFlexEntityBulkSubscription>(FLEX_ENTITY_BULK_SUBSCRIPTION);
	const { data: { documentThreadUpdated } = {} } = useDataSubscription<OnDocumentThreadUpdatedSubscription>(DOCUMENT_THREAD_SUBSCRIPTION);
	const activeAgreement = useRecoilValue(activeAgreementReport);
	const setUploadState = useSetRecoilState(upload);
	const setBulkState = useSetRecoilState(bulk);
	const setAgreementsExecutionState = useSetRecoilState(agreementsExecution);
	const setAgreementsExecutionTotalCountValue = useSetRecoilState(agreementExecutionTotalCount);
	const setIsDataCreationDisabled = useSetRecoilState(isDataCreationDisabled);
	const setIncomingMessages = useSetRecoilState(incomingMessages);

	useEffect(() => {
		if (agreements && agreements.agreementCreated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Agreement" });
			client.cache.evict({ fieldName: "GetAgreements" });
			client.cache.evict({ fieldName: "GetAgreementOverview" });
			client.cache.evict({ fieldName: "GetAgreementsCount" });
			client.cache.evict({ fieldName: "agreementTableRows" });
		}
		if (
			agreement &&
			agreement.agreementRun &&
			agreement.agreementRun.isAgreementRunSubscriptionActive &&
			agreement.agreementRun.tenantIds.includes(tenantId)
		) {
			switch (agreement.agreementRun.status) {
				case AgreementRunStatus.Executed: {
					activeAgreement === agreement.agreementRun.id && client.cache.evict({ fieldName: "GetAgreementReportData" });
					setAgreementsExecutionState((currentValue) => [
						...currentValue.filter((current) => current.id !== agreement.agreementRun.id),
						agreement.agreementRun,
					]);
					break;
				}
				case AgreementRunStatus.InExecution: {
					setAgreementsExecutionState((currentValue) => [...currentValue, agreement.agreementRun]);
					break;
				}
			}
		}
	}, [agreement, agreements]);

	useEffect(() => {
		if (
			plannedAgreementsRun &&
			plannedAgreementsRun.agreementsRunPlanned.initiatorUserId === userId &&
			plannedAgreementsRun.agreementsRunPlanned.isAgreementRunSubscriptionActive
		) {
			setAgreementsExecutionTotalCountValue(plannedAgreementsRun.agreementsRunPlanned.ids);
		}
	}, [plannedAgreementsRun, setAgreementsExecutionTotalCountValue, userId]);

	useEffect(() => {
		if (cases && cases.caseCreated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Case" });
			client.cache.evict({ fieldName: "GetCaseCount" });
			client.cache.evict({ fieldName: "caseTableRows" });
		}
		if (forms && forms.formCreated.includes(tenantId)) {
			client.cache.evict({ fieldName: "flexEntities" });
			client.cache.evict({ fieldName: "formTableRows" });
			client.cache.evict({ fieldName: "GetFormFlexEntities" });
			client.cache.evict({ fieldName: "GetFlexEntitiesCount" });
			client.cache.evict({ fieldName: "GetFlexEntityTypeAndCasesById" });
			client.cache.evict({ fieldName: "cases" });
		}
		if (records && records.recordUpdated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Record" });
			client.cache.evict({ fieldName: "GetRecordsCount" });
			client.cache.evict({ fieldName: "GetFlexEntityAndTypeByInstanceId" });
			client.cache.evict({ fieldName: "recordTableRows" });
		}
		if (refunds && refunds.refundCreated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Refund" });
			client.cache.evict({ fieldName: "GetRefunds" });
			client.cache.evict({ fieldName: "GetRefundCount" });
			client.cache.evict({ fieldName: "refundTableRows" });
		}
		if (documents && documents.documentCreated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Document" });
			client.cache.evict({ fieldName: "GetDocuments" });
			client.cache.evict({ fieldName: "GetDocumentsCount" });
			client.cache.evict({ fieldName: "Refund" });
			client.cache.evict({ fieldName: "documentTableRows" });
			client.cache.evict({ fieldName: "documents" });
		}
		if (simulations && simulations.simulationUpdated.includes(tenantId)) {
			client.cache.evict({ fieldName: "GetSimulations" });
		}
		if (tasks && tasks.taskUpdated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Task" });
		}
		if (quests && quests.questUpdated.includes(tenantId)) {
			client.cache.evict({ fieldName: "Quest" });
			client.cache.evict({ fieldName: "GetQuests" });
			client.cache.evict({ fieldName: "GetInboxQuests" });
			client.cache.evict({ fieldName: "questTableRows" });
		}
		if (messages && messages.threadUpdated.includes(tenantId)) {
			client.cache.evict({ fieldName: "GetMessages" });
			client.cache.evict({ fieldName: "GetThreadMessageCount" });
		}

		if (
			flexEntityUpload &&
			flexEntityUpload.flexEntityUploading.tenantId === tenantId &&
			flexEntityUpload.flexEntityUploading.uploadedById === userId
		) {
			//	check status to display
			setUploadState((currentVal) => ({
				...currentVal,
				...flexEntityUpload.flexEntityUploading,
				progress: flexEntityUpload.flexEntityUploading.percentage,
			}));
		}

		if (flexEntityBulk && flexEntityBulk.flexEntityBulk.tenantId === tenantId) {
			if (flexEntityBulk.flexEntityBulk.uploadedById === userId) {
				const progress = flexEntityBulk.flexEntityBulk.percentage || 0;
				const casesCreated = flexEntityBulk.flexEntityBulk.casesCreated || [];
				const patientsCreated = flexEntityBulk.flexEntityBulk.patientsCreated || [];
				const formsCreated = flexEntityBulk.flexEntityBulk.formsCreated || [];

				setBulkState((currentVal: any) => ({
					...currentVal,
					isDisplayed: !!currentVal.status,
					status: currentVal.status,
					progress,
					casesCreated,
					patientsCreated,
					formsCreated,
				}));
			}
			if ((flexEntityBulk.flexEntityBulk.percentage || 0) < 100) {
				setIsDataCreationDisabled(true);
			}
			if (flexEntityBulk.flexEntityBulk.percentage === 100) {
				setIsDataCreationDisabled(false);
			}
		}

		if (documentThreadUpdated && documentThreadUpdated) {
			setIncomingMessages((current) => [...current, documentThreadUpdated]);
		}
	}, [
		activeAgreement,
		cases,
		forms,
		records,
		refunds,
		documents,
		tasks,
		quests,
		client.cache,
		messages,
		simulations,
		flexEntityUpload,
		flexEntityBulk,
		documentThreadUpdated,
		setIncomingMessages,
	]);

	return null;
}
